import gql from 'graphql-tag';
import {
  BLUEPRINT_FIELDS,
  FATVARIANT,
  MANUALPAYLOAD,
  MODULE_FIELDS,
  RETAILER_FIELDS,
  VARIANT_FIELDS,
} from '@src/app/services/apollo/apollo.fragments';

export const QUERIES = {
  currentUser: gql`
    query GetCurrentUser {
      currentUser {
        id
        email
        roles {
          children {
            children {
              name
              permissions {
                bit
                name
              }
            }
          }
          deleted
          name
          permissions {
            bit
            name
          }
        }
        totpQr
        scopes
      }
    }
  `,
  allUsers: gql`
    query GetAllUsers($blocked: Boolean!, $first: Int, $drop: Int, $sort: [RequestSort!]) {
      allUsers(blocked: $blocked, first: $first, drop: $drop, sort: $sort) {
        edges {
          node {
            id
            email
            blocked
            roles {
              name
            }
            scopes
          }
        }
        pageInfo {
          hasNextPage
        }
        count
      }
    }
  `,
  allRetailers: gql`
    ${RETAILER_FIELDS}
    query GetAllRetailers($first: Int, $drop: Int, $sort: [RequestSort!]) {
      allRetailers(first: $first, drop: $drop, sort: $sort) {
        edges {
          node {
            ...retailerFragment
          }
        }
        pageInfo{
          hasNextPage
        }
        count
      }
    }
  `,
  allPublicRetailers: gql`
    query GetAllPublicRetailers($first: Int, $drop: Int) {
      allPublicRetailers(first: $first, drop: $drop) {
        edges {
          node {
            id
            name
          }
        }
        pageInfo{
          hasNextPage
        }
        count
      }
    }
  `,
  currentRetailer: gql`
    ${RETAILER_FIELDS}
    query GetCurrentRetailer {
      currentRetailer {
        ...retailerFragment
      }
    }
  `,
  allBlueprints: gql`
    ${BLUEPRINT_FIELDS}
    query GetAllModuleBlueprints {
      allBlueprints {
        ...blueprintFragment
      }
    }
  `,
  allBlueprintsWithVariants: gql`
    ${FATVARIANT}
    query GetAllFatVariants {
        allBlueprints {
            ...extendedBlueprintFragment
        }
    }
  `,
  allBlueprintsWithVariantsWithKey: gql`
    ${FATVARIANT}
    query GetAllFatVariantsWithKey {
        allBlueprintsWithKey {
            ...extendedBlueprintFragment
        }
    }
  `,
  fatVariantsByVariantIdsWithKey: gql`
    ${FATVARIANT}
    query GetFatVariantsByVariantIdsWithKey($variantIds: [String!]!, $woodType: String!) {
      fatVariantsByVariantIdsWithKey(variantIds: $variantIds, woodType: $woodType) {
        ...extendedBlueprintFragment
      }
    }
  `,
  fatVariantsByVariantIds: gql`
      ${FATVARIANT}
      query GetFatVariantsByVariantIds($variantIds: [String!]!, $woodType: String!) {
          fatVariantsByVariantIds(variantIds: $variantIds, woodType: $woodType) {
              ...extendedBlueprintFragment
          }
      }
  `,
  allCurrentBlueprints: gql`
    ${BLUEPRINT_FIELDS}
    query GetCurrentModuleBlueprints {
      allCurrentBlueprints {
        ...blueprintFragment
      }
    }
  `,
  allArchivedBlueprints: gql`
    ${BLUEPRINT_FIELDS}
    query GetArchivedModuleBlueprints {
      allBlueprintsArchived {
        ...blueprintFragment
      }
    }
  `,
  blueprintsByName: gql`
    ${BLUEPRINT_FIELDS}
    query GetBlueprintsByName($name: String!) {
      blueprintsByName(name: $name) {
        ...blueprintFragment
      }
    }
  `,
  variantsByBlueprintId: gql`
    ${VARIANT_FIELDS}
    query GetVariantsByBlueprintId($id: String!) {
      variantsByBlueprintId(id: $id) {
        ...variantFragment
      }
    }
  `,
  configuration: gql`
    query GetConfigurationByCode($code: String!) {
      configuration(code: $code) {
        id
        code
        name
        scope
        configurationPlacement {
            placements {
                id
                variantId
                transformation {
                    r1 {
                        c1, c2, c3, c4
                    }
                    r2 {
                        c1, c2, c3, c4
                    }
                    r3 {
                        c1, c2, c3, c4
                    }
                    r4 {
                        c1, c2, c3, c4
                    }
                }
                xAttachments {
                    values {
                        key
                        value {
                            placementId
                            yAttachmentId
                        }
                    }
                }
            }
            originPlacement
            woodType
        }
        email
        isPreConfiguration
      }
    }
  `,
  roles: gql`
    query GetRoles {
      allRoleNames
    }
  `,
  moduleInvariantTemplates: gql`
    query GetModuleInvariantTemplates {
      moduleInvariantTemplates {
        id
        name
        invariants {
          name
          correction
          rule
          id
          translations {
            values {
              key
              value
            }
          }
          translationKeys {
            key
            value
          }
          disableDuringOnChange
        }
      }
    }
  `,
  envInvariantTemplates: gql`
    query GetEnvInvariantTemplates {
      envInvariantTemplates {
        id
        name
        invariants {
          name
          correction
          rule
          id
          translations {
            values {
              key
              value
            }
          }
          translationKeys {
            key
            value
          }
          disableDuringOnChange
        }
      }
    }
  `,
  moduleEventTemplates: gql`
    query GetModuleEventTemplates {
      moduleEventTemplates {
        id
        name
        events {
          onAttach
          onDelete
          onCreated
          onChange
        }
      }
    }
  `,
  allModuleCategorySettings: gql`
    query GetAllModuleCategorySettings($archived: Boolean!) {
      allModuleCategorySettings(withArchivedBlueprints: $archived) {
        id
        reference
        order
        customizable
        blueprints {
          name
          status
          moduleVariantIds
          orderWeight
        }
      }
    }
  `,
  catalog: gql`
    query GetCatalog {
      catalogSetting {
        id
        categories {
          title
          moduleCategoryIds
        }
        filterSettings{
          values {
            moduleCategoryReference: key
            value {
              attributes {
                values {
                  attributeId: key
                  value {
                    valueIcons {
                      values {
                        attributeValue: key
                        iconAsset: value {
                          id
                          filename
                          directory
                          mimeType
                          url
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `,
  allAttributes: gql`
    ${MODULE_FIELDS}
    query GetAllAttributes($first: Int, $drop: Int, $sort: [RequestSort!]) {
      allAttributes(first: $first, drop: $drop, sort: $sort) {
        edges {
          node {
            ...attributeFragment
          }
        }
        pageInfo{
          hasNextPage
        }
        count
      }
    }
  `,
  attribute: gql`
    ${MODULE_FIELDS}
    query GetAttributeById($id: String!) {
      attribute(id: $id) {
        ...attributeFragment
      }
    }
  `,
  filteredTranslations: gql`
      query GetFilteredTranslations {
          filteredTranslations {
              key
              value
          }
      }
  `,
  defaultTranslations: gql`
    query GetDefaultTranslations {
      defaultTranslations {
        key
        value
      }
    }
  `,
  variantTranslations: gql`
    query GetVariantTranslations {
        variantTranslations {
            bpId
            bpName
            variants {
                varGroupId
                varName
                translations {
                    key
                    value
                }
            }
        }
    }
  `,
  translationUpdatingStatus: gql`
      query GetTranslationUpdatingStatus {
          translationUpdatingStatus
      }
  `,
  allMaterials: gql`
    query GetAllMaterials($first: Int, $drop: Int) {
      allMaterials(first: $first, drop: $drop) {
        count
        edges {
          node {
            id
            name
            color
            roughness
            metalness
            map
            lightMap
            lightMapIntensity
            aoMap
            aoMapIntensity
            emissive
            emissiveIntensity
            emissiveMap
            bumpMap
            bumpScale
            normalMap
            normalMapType
            normalScale {
              x
              y
            }
            displacementMap
            displacementScale
            displacementBias
            roughnessMap
            metalnessMap
            alphaMap
            envMap
            envMapIntensity
            wireframe
            wireframeLinewidth
            fog
            flatShading
            alphaTest
            alphaToCoverage
            blendDst
            blendDstAlpha
            blendEquation
            blendEquationAlpha
            blending
            blendSrc
            blendSrcAlpha
            clipIntersection
            clippingPlanes {
              normal {
                x
                y
                z
              }
              constant
            }
            clipShadows
            colorWrite
            depthFunc
            depthTest
            depthWrite
            opacity
            polygonOffset
            polygonOffsetFactor
            polygonOffsetUnits
            precision
            premultipliedAlpha
            dithering
            side
            shadowSide
            toneMapped
            transparent
            vertexColors
            visible
            format
            stencilWrite
            stencilFunc
            stencilRef
            stencilWriteMask
            stencilFuncMask
            stencilFail
            stencilZFail
            stencilZPass
          }
          cursor
        }
      }
    }
  `,
  allTextures: gql`
    query GetAllTextures($first: Int, $drop: Int) {
      allTextures(first: $first, drop: $drop) {
        count
        edges {
          node {
            id
            name
            asset {
              id
              filename
              mimeType
              directory
              url
            }
          }
          cursor
        }
      }
    }
  `,
  getRetailerApiKey: gql`
    query GetRetailerApiKey($retailerId: String!) {
      retailerApiKey(id: $retailerId)
    }
  `,
  getFreeEans: gql`
    query GetFreeEans {
        freeEANs
    }
  `,
  getDocumentationBySubjectType: gql`
        query GetDocumentationBySubjectType($subjectType: String!) {
            documentationBySubjectType(subjectType: $subjectType) {
                id
                subjectType
                fields {
                    fieldName
                    fieldType
                    required
                }
                comment
            }
        }
    `,
};

export const MUTATIONS = {
  userMutation: gql`
    mutation UploadUser($user: UserInput!) {
      user {
        uploadUser(user: $user)
      }
    }
  `,
  passwordForgotten: gql`
    mutation PasswordForgotten ($email: String!) {
      user {
        id
        passwordForgotten(email: $email)
      }
    }
  `,
  resetMfaMutation: gql`
    mutation ResetMfaMutation ($userId: Id!) {
      user {
        id
        resetMfa(userId: $userId)
      }
    }
  `,
  addRetailerMutation: gql`
    ${RETAILER_FIELDS}  
    mutation AddRetailer($retailer: RetailerInput!) {
      retailer {
          newRetailer(retailer: $retailer) {
              ...retailerFragment
          }
      }
    }
  `,
  copyRetailerMutation: gql`
      ${RETAILER_FIELDS}
      mutation CopyRetailer($srcId: Id!, $retailer: RetailerInput!) {
          retailer {
              copyRetailer(srcId: $srcId, retailer: $retailer) {
                  ...retailerFragment
              }
          }
      }
  `,
  updateRetailerMutation: gql`
    mutation UpdateRetailer($id: Id!, $retailer: RetailerInput!) {
      retailer {
        updateRetailer(id: $id, retailer: $retailer)
      }
    }
  `,
  singlePriceStockMutation: gql`
    mutation NewSinglePriceStocks($priceStocks: [SinglePriceStockInput!]!, $scopes: [String!]) {
      retailer {
        newSinglePriceStocks(priceStocks: $priceStocks, scopes: $scopes)
      }
    }
  `,
  woodPriceStockMutation: gql`
    mutation NewWoodPriceStocks($priceStocks: [WoodPriceStockInput!]!, $scopes: [String!]) {
      retailer {
        newWoodPriceStocks(priceStocks: $priceStocks, scopes: $scopes)
      }
    }
  `,
  addBlueprintMutation: gql`
    mutation AddBlueprint($blueprint: ModuleBlueprintInput!) {
      module {
        newBlueprint(blueprint: $blueprint)
      }
    }
  `,
  updateBlueprintMutation: gql`
    mutation UpdateBlueprint($id: Id!, $blueprint: ModuleBlueprintInput!) {
      module {
        updateBlueprint(id: $id, blueprint: $blueprint)
      }
    }
  `,
  copyBlueprintMutation: gql`
    mutation CopyBlueprint($id: Id!, $blueprint: ModuleBlueprintInput!) {
      module {
        copyBlueprint(id: $id, blueprint: $blueprint)
      }
    }
  `,
  deleteBlueprintMutation: gql`
    mutation DeleteBlueprint($blueprintId: Id!) {
      module {
        deleteBlueprint(id: $blueprintId)
      }
    }
  `,
  archiveBlueprintMutation: gql`
    mutation ArchiveBlueprint($blueprintId: Id!) {
      module {
        archiveBlueprint(id: $blueprintId)
      }
    }
  `,
  publishBlueprintMutation: gql`
    mutation PublishBlueprint($blueprintId: Id!) {
      module {
        publishBlueprint(id: $blueprintId)
      }
    }
  `,
  variantsMutation: gql`
    mutation UploadVariants($blueprintId: Id!, $variants: [ModuleVariantInput!]!) {
      module {
        uploadVariants(blueprintId: $blueprintId, variants: $variants)
      }
    }
  `,
  blockUserMutation: gql`
    mutation BlockUser($userId: Id!, $block: Boolean!) {
      user {
        block(userId: $userId, block: $block)
      }
    }
  `,
  updateConfigurationMutation: gql`
    mutation UpdateConfiguration(
      $code: String!,
      $setToPreConfiguration: Boolean!, 
      $forcePriceRefresh: Boolean!, 
      $scopes: [String!], 
      $newCode: String, 
      $name: String
    ) {
      configuration {
        updateConfigurationPortal(
          code: $code,
          setToPreConfiguration: $setToPreConfiguration,
          forcePriceRefresh: $forcePriceRefresh,
          scopes: $scopes,
          newCode: $newCode,
          name: $name
        )
      }
    }
  `,
  setRolesMutation: gql`
    mutation SetRoles($userId: Id!, $roles: [String!]!) {
      user {
        setRoles(userId: $userId, roles: $roles)
      }
    }
  `,
  setScopesMutation: gql`
    mutation SetScopes($userId: Id!, $scopes: [String!]!) {
      user {
        setScopes(userId: $userId, scopes: $scopes)
      }
    }
  `,
  newEventTemplate: gql`
    mutation NewEventTemplate($eventTemplate: EventTemplateInput!) {
      module {
        newEventTemplate(eventTemplate: $eventTemplate)
      }
    }
  `,
  updateEventTemplate: gql`
    mutation UpdateEventTemplate($id: Id!, $eventTemplate: EventTemplateInput!) {
      module {
        updateEventTemplate(id: $id, eventTemplate: $eventTemplate)
      }
    }
  `,
  newModuleInvariantTemplate: gql`
    mutation NewModuleInvariantTemplate($invariantTemplate: BlueprintInvariantTemplateInput!) {
      module {
        newInvariantTemplate(invariantTemplate: $invariantTemplate)
      }
    }
  `,
  updateModuleInvariantTemplate: gql`
    mutation UpdateModuleInvariantTemplate($id: Id!, $invariantTemplate: BlueprintInvariantTemplateInput!) {
      module {
        updateInvariantTemplate(id: $id, invariantTemplate: $invariantTemplate)
      }
    }
  `,
  newEnvInvariantTemplate: gql`
    mutation NewEnvInvariantTemplate($invariantTemplate: SafetyEnvironmentInvariantTemplateInput!) {
      retailer {
        newInvariantTemplate(invariantTemplate: $invariantTemplate)
      }
    }
  `,
  updateEnvInvariantTemplate: gql`
    mutation UpdateEnvInvariantTemplate($id: Id!, $invariantTemplate: SafetyEnvironmentInvariantTemplateInput!) {
      retailer {
        updateInvariantTemplate(id: $id, invariantTemplate: $invariantTemplate)
      }
    }
  `,
  updateCatalogMutation: gql`
    mutation UpdateCatalogMutation($catalog: CatalogInput!, $scopes: [String!]) {
      module {
        newCatalog(catalog: $catalog, scopes: $scopes)
      }
    }
  `,
  upsertModuleCategory: gql`
    mutation UpsertModuleCategoryMutation($moduleCategory: ModuleCategoryInput!) {
      module {
        uploadModuleCategory(moduleCategory: $moduleCategory)
      }
    }
  `,
  newAttributeMutation: gql`
    mutation NewAttribute($attribute: AttributeInput!) {
      module {
        newAttribute(attribute: $attribute)
      }
    }
  `,
  updateAttributeMutation: gql`
    mutation UpdateAttribute($id: Id!, $attribute: AttributeInput!) {
      module {
        updateAttribute(id: $id, attribute: $attribute)
      }
    }
  `,
  setVariantRetailerTranslationsMutation: gql`
    mutation SetVariantRetailerTranslations ($ti: [LocalizedVariantInput!]!) {
      translations {
        setVariantRetailerTranslations(ti: $ti)
      }
    }
  `,
  setAppRetailerTranslationsMutation: gql`
    mutation SetAppRetailerTranslations ($ti: LocalizedStringForAppInput!) {
      translations {
        setAppRetailerTranslations(ti: $ti)
      }
    }
  `,
  setAppDefaultTranslationsMutation: gql`
    mutation SetAppDefaultTranslations ($ti: LocalizedStringForAppInput!) {
      translations {
        setAppDefaultTranslations(ti: $ti)
      }
    }
  `,
  setInvariantRetailerTranslationsMutation: gql`
    mutation SetInvariantRetailerTranslations ($ti: LocalizedStringForInvariantInput!) {
      translations {
        setInvariantRetailerTranslations(ti: $ti)
      }
    }
  `,
  setAttributeRetailerTranslationsMutation: gql`
    mutation SetAttributeRetailerTranslations ($ti: LocalizedStringForAttributeInput!) {
      translations {
        setAttributeRetailerTranslations(ti: $ti)
      }
    }
  `,
  newMaterial: gql`
    mutation NewMaterial($material: MaterialInput!) {
      module {
        newMaterial(material: $material)
      }
    }
  `,
  updateMaterial: gql`
    mutation UpdateMaterial($id: Id!, $material: MaterialInput!) {
      module {
        updateMaterial(id: $id, material: $material)
      }
    }
  `,
  deleteMaterial: gql`
    mutation DeleteMaterial($id: Id!) {
      module {
        deleteMaterial(id: $id)
      }
    }
  `,
  newTexture: gql`
    mutation NewTexture($texture: TextureInput!) {
      module {
        newTexture(texture: $texture)
      }
    }
  `,
  updateTexture: gql`
    mutation UpdateTexture($id: Id!, $texture: TextureInput!) {
      module {
        updateTexture(id: $id, texture: $texture)
      }
    }
  `,
  deleteTexture: gql`
    mutation DeleteTexture($id: Id!) {
      module {
        deleteTexture(id: $id)
      }
    }
  `,
  makeConfigurationWithVariant: gql`
    mutation MakeConfigWithVariant($variantId: Id!) {
      configuration {
        makeConfigWithVariant(variantId: $variantId)
      }
    }
  `,
  getManualPayload: gql`
    ${MANUALPAYLOAD}
    mutation GenerateManualPayload($code: String!, $placementsInfo: IdModulePlacementListSpacialPositionInputInputMap!) {
        manual {
            generateManualPayload(code: $code, placementsInfo: $placementsInfo) {
                ...configurationManualPayloadFragment
            }
        }
    }
  `,
  getManualPayloadWithKey: gql`
    ${MANUALPAYLOAD}
    mutation GenerateManualPayloadWithKey($code: String!, $placementsInfo: IdModulePlacementListSpacialPositionInputInputMap!) {
        manual {
            generateManualPayloadWithKey(code: $code, placementsInfo: $placementsInfo) {
                ...configurationManualPayloadFragment
            }
        }
    }
  `,
  uploadEANsForRetailer: gql`
      mutation uploadEANsForRetailer($id: Id!, $eans: [String!]!) {
          retailer {
              uploadEANsForRetailer(id: $id, eans: $eans)
          }
      }
  `,
  updateDocumentation: gql`
      mutation updateDocumentation($id: Id!, $documentation: DocumentationInput!) {
          module {
              updateDocumentation(id: $id, documentation: $documentation)
          }
      }
  `,
};
